/**
 * Hook per gestire le estensioni dell'utente (seller o buyer).
 * Verifica se un'estensione è attiva e gestisce i sotto-moduli.
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Context per le estensioni
const ExtensionsContext = createContext(null);

/**
 * Provider per le estensioni.
 * Wrappa l'app per rendere disponibili le info sulle estensioni ovunque.
 */
export const ExtensionsProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [features, setFeatures] = useState({});
  const [config, setConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carica le estensioni dell'utente (seller o buyer)
  const loadExtensions = useCallback(async () => {
    // Non caricare se non c'è un utente autenticato
    if (!token || !user) {
      setSubscriptions([]);
      setFeatures({});
      setConfig([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const res = await axios.get(`${API}/extensions/my`);
      setSubscriptions(res.data.subscriptions || []);
      setFeatures(res.data.features || {});
      setConfig(res.data.config || []);
      setError(null);
    } catch (err) {
      // Se non è autorizzato, ignora l'errore
      if (err.response?.status === 403 || err.response?.status === 401) {
        setSubscriptions([]);
        setFeatures({});
      } else {
        console.error('Error loading extensions:', err);
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  // Carica quando l'utente cambia o al mount
  useEffect(() => {
    loadExtensions();
  }, [loadExtensions]);

  // Verifica se un'estensione è attiva
  const isExtensionActive = useCallback((extensionCode) => {
    const sub = subscriptions.find(s => s.extension === extensionCode);
    return sub?.enabled === true && sub?.status !== 'expired';
  }, [subscriptions]);

  // Verifica se un'estensione è in trial
  const isExtensionTrial = useCallback((extensionCode) => {
    const sub = subscriptions.find(s => s.extension === extensionCode);
    return sub?.is_trial === true && sub?.status === 'trial';
  }, [subscriptions]);

  // Ottiene i giorni rimanenti del trial
  const getTrialDaysRemaining = useCallback((extensionCode: string) => {
    const sub = subscriptions.find(s => s.extension === extensionCode);
    if (!sub?.trial_ends_at) return null;
    
    const endDate = new Date(sub.trial_ends_at);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [subscriptions]);

  // Verifica se un sotto-modulo è attivo
  const isFeatureActive = useCallback((extensionCode, featureCode) => {
    if (!isExtensionActive(extensionCode)) return false;
    
    const extFeatures = features[extensionCode];
    if (!extFeatures) return false;
    
    // Per logistica
    if (extensionCode === 'ext_logistics' && extFeatures.logistics) {
      return extFeatures.logistics[featureCode] === true;
    }
    
    return false;
  }, [features, isExtensionActive]);

  // Ottiene la configurazione di un'estensione
  const getExtensionConfig = useCallback((extensionCode) => {
    return config.find(c => c.extension === extensionCode);
  }, [config]);

  // Aggiorna le features di un'estensione
  const updateFeatures = useCallback(async (extensionCode, newFeatures) => {
    try {
      await axios.put(`${API}/extensions/features/${extensionCode}`, newFeatures);
      await loadExtensions(); // Ricarica
      return { success: true };
    } catch (err) {
      console.error('Error updating features:', err);
      return { success: false, error: err.response?.data?.detail || err.message };
    }
  }, [loadExtensions]);

  // Avvia un trial
  const startTrial = useCallback(async (extensionCode) => {
    try {
      const res = await axios.post(`${API}/extensions/trial/${extensionCode}`);
      await loadExtensions(); // Ricarica
      return { success: true, data: res.data };
    } catch (err) {
      console.error('Error starting trial:', err);
      return { success: false, error: err.response?.data?.detail || err.message };
    }
  }, [loadExtensions]);

  const value = {
    subscriptions,
    features,
    config,
    loading,
    error,
    isExtensionActive,
    isExtensionTrial,
    getTrialDaysRemaining,
    isFeatureActive,
    getExtensionConfig,
    updateFeatures,
    startTrial,
    reload: loadExtensions
  };

  return (
    <ExtensionsContext.Provider value={value}>
      {children}
    </ExtensionsContext.Provider>
  );
};

/**
 * Hook per usare le estensioni.
 */
export const useExtensions = () => {
  const context = useContext(ExtensionsContext);
  if (!context) {
    throw new Error('useExtensions must be used within ExtensionsProvider');
  }
  return context;
};

/**
 * Hook semplificato per verificare una singola estensione.
 * Utile per controlli rapidi.
 */
export const useExtension = (extensionCode) => {
  const { isExtensionActive, isExtensionTrial, getTrialDaysRemaining, loading } = useExtensions();
  
  return {
    isActive: isExtensionActive(extensionCode),
    isTrial: isExtensionTrial(extensionCode),
    trialDaysRemaining: getTrialDaysRemaining(extensionCode),
    loading
  };
};

/**
 * Hook per verificare un sotto-modulo specifico.
 */
export const useFeature = (extensionCode, featureCode) => {
  const { isFeatureActive, isExtensionActive, loading } = useExtensions();
  
  return {
    isActive: isFeatureActive(extensionCode, featureCode),
    extensionActive: isExtensionActive(extensionCode),
    loading
  };
};

export default useExtensions;
